/**
 * Section Charges Fixes pour le modal Paramètres Trésorerie
 * @module components/tresorerie/ChargesFixesSection
 */

import { useState } from 'react';
import { Plus, Loader2, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useChargesFixes } from '@/hooks/useChargesFixes';
import { ChargesFixesList } from './ChargesFixesList';
import { ChargesFixesForm } from './ChargesFixesForm';
import { BulkChargesFixesModal } from './BulkChargesFixesModal';
import type { ChargeFix, CreateChargeFixData, UpdateChargeFixData } from '@/types/charges-fixes.types';

interface ChargesFixesSectionProps {
  entrepriseId: string | null;
}

/**
 * Formate un montant en euros
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Section de gestion des charges fixes - Style Centre de Pilotage
 */
export function ChargesFixesSection({ entrepriseId }: ChargesFixesSectionProps) {
  const {
    chargesFixes,
    isLoading,
    totalMensuel,
    ajouterChargesFixes,
    modifierChargeFix,
    supprimerChargeFix,
    toggleActif,
  } = useChargesFixes(entrepriseId);

  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<ChargeFix | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Ouvrir le modal bulk pour ajouter plusieurs charges
  const handleOpenBulkModal = () => {
    setIsBulkModalOpen(true);
  };

  // Ouvrir le formulaire en mode édition
  const handleOpenEdit = (charge: ChargeFix) => {
    setEditingCharge(charge);
    setIsEditFormOpen(true);
  };

  // Fermer le formulaire d'édition
  const handleCloseEditForm = () => {
    setIsEditFormOpen(false);
    setEditingCharge(null);
  };

  // Soumettre le formulaire d'édition
  const handleEditSubmit = async (data: CreateChargeFixData) => {
    if (!editingCharge) return;
    await modifierChargeFix(editingCharge.id, data as UpdateChargeFixData);
  };

  // Soumettre le bulk modal
  const handleBulkSubmit = async (data: CreateChargeFixData[]): Promise<boolean> => {
    setIsSaving(true);
    try {
      const success = await ajouterChargesFixes(data);
      return success;
    } finally {
      setIsSaving(false);
    }
  };

  // Ne pas afficher si pas d'entrepriseId
  if (!entrepriseId) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Header avec total mensuel */}
      <div className="flex items-center justify-between p-4 rounded-xl border border-blue-500/30 bg-blue-500/10">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Receipt className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">Charges fixes récurrentes</h3>
            <p className="text-sm text-white/50">
              Vos dépenses mensuelles automatiques
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-white/40 uppercase tracking-wide">Total mensuel</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(totalMensuel)}</p>
        </div>
      </div>

      {/* Liste des charges */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-white/40" />
        </div>
      ) : (
        <ScrollArea className="max-h-[180px]">
          <ChargesFixesList
            chargesFixes={chargesFixes}
            onEdit={handleOpenEdit}
            onDelete={supprimerChargeFix}
            onToggleActif={toggleActif}
          />
        </ScrollArea>
      )}

      {/* Bouton ajouter */}
      <Button
        variant="outline"
        className="w-full border-white/[0.1] text-white/70 hover:bg-white/[0.05] hover:text-white"
        onClick={handleOpenBulkModal}
      >
        <Plus className="h-4 w-4 mr-2" />
        Ajouter des charges fixes
      </Button>

      {/* Modal bulk pour ajouter plusieurs charges */}
      {entrepriseId && (
        <BulkChargesFixesModal
          open={isBulkModalOpen}
          onOpenChange={setIsBulkModalOpen}
          entrepriseId={entrepriseId}
          onSubmit={handleBulkSubmit}
          isLoading={isSaving}
        />
      )}

      {/* Formulaire modal pour éditer une charge */}
      <ChargesFixesForm
        isOpen={isEditFormOpen}
        onClose={handleCloseEditForm}
        onSubmit={handleEditSubmit}
        chargeFix={editingCharge}
      />
    </div>
  );
}
